'use client'

import { Typography } from '@/ui/atoms/typography/Typography'
import { Sparkles } from 'lucide-react'
import Image from 'next/image'
import type { SponsoredCampaign } from '../hooks/useSponsoredSpendStatus'

interface SponsoredSpendBadgeProps {
  campaign: SponsoredCampaign
  variant?: 'inline' | 'card'
}

export function SponsoredSpendBadge({ campaign, variant = 'inline' }: SponsoredSpendBadgeProps) {
  const remainingDollars = (campaign.remainingCents / 100).toFixed(2)
  const percentLabel = `${campaign.sponsorshipPercentage}% sponsored`

  if (variant === 'card') {
    return (
      <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-gradient-yellow/20 to-gradient-green/20">
            {campaign.sponsorLogo ? (
              <Image src={campaign.sponsorLogo} alt={campaign.sponsorName ?? 'Sponsor'} width={24} height={24} className="rounded" />
            ) : (
              <Sparkles className="h-5 w-5 text-primary" />
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Typography variant="p" className="font-semibold text-sm">
                {campaign.campaignName ?? 'Sponsored Spend'}
              </Typography>
              {campaign.sponsorName && (
                <Typography variant="p" className="text-muted-foreground text-xs">
                  by {campaign.sponsorName}
                </Typography>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs">
              <span className="text-primary">{percentLabel}</span>
              <span className="text-muted-foreground">${remainingDollars} remaining</span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Inline variant — compact badge
  return (
    <div className="flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-2.5 py-1">
      <Sparkles className="h-3 w-3 text-primary" />
      <span className="font-medium text-primary text-xs">{percentLabel}</span>
      {campaign.sponsorName && (
        <>
          <span className="text-muted-foreground text-xs">by</span>
          <span className="font-medium text-xs">{campaign.sponsorName}</span>
        </>
      )}
    </div>
  )
}
